import DOMPurify from 'dompurify';

// Sanitization config - restrict to safe formatting tags only
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'span', 'div', 'u'],
  ALLOWED_ATTR: ['style', 'class'],
  ALLOWED_STYLE_PROPERTIES: ['color', 'text-align', 'background-color'],
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
};

/**
 * Safely renders HTML content with XSS protection.
 * Falls back to plain text for non-HTML content.
 *
 * @param {string} content - The content to render (HTML or plain text)
 * @param {string} className - Optional CSS class name
 * @param {string} fallback - Optional fallback text when content is empty
 */
const SafeHTMLRenderer = ({ content, className = '', fallback = 'No description available.' }) => {
  // Check if content contains HTML tags
  const isHTML = (str) => {
    if (!str || typeof str !== 'string') return false;
    return /<[a-z][\s\S]*>/i.test(str);
  };

  // Empty content - show fallback
  if (!content || (typeof content === 'string' && content.trim() === '')) {
    return <span className={className}>{fallback}</span>;
  }

  // HTML content - sanitize and render
  if (isHTML(content)) {
    const sanitizedHTML = DOMPurify.sanitize(content, SANITIZE_CONFIG);
    return (
      <div
        className={`rich-text-content ${className}`}
        dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
      />
    );
  }

  // Plain text content - render as-is
  return <span className={className}>{content}</span>;
};

/**
 * Utility function to get plain text length from HTML content
 * Useful for "Read More" truncation logic
 */
export const getPlainTextLength = (html) => {
  if (!html || typeof html !== 'string') return 0;
  return html.replace(/<[^>]*>/g, '').length;
};

/**
 * Utility function to get plain text from HTML
 * Strips all HTML tags
 */
export const stripHTML = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html.replace(/<[^>]*>/g, '');
};

export default SafeHTMLRenderer;
